import { useState } from 'react';

import { ApiError } from '@solar/api-client';
import { ErrorBox, Spinner, useAsync } from '@solar/ui';

import { api } from '../api';
import { Modal } from '../Modal';

/**
 * 주문 목록 일괄 처리 — 레거시 list.blade.php 445-488행 버튼 이식.
 *
 * 역할별 노출 조건은 레거시 그대로다:
 * - 보류: 1,2,6,7 / 접수완료·접수완료취소: 1,2
 * - 보조금 지급(구)·구보조금 취소: 7 / 보조금 지급(시)·시보조금 취소: 6
 * - 제출 하기·기사배정: 3,5
 * 실제 권한·상태 검증은 서버가 하고(403/409/422), 여기서는 메시지를 그대로 보여준다.
 * 전체 성공 아니면 전체 실패(부분 성공 없음)도 서버 규칙이다.
 */

type Action =
  | { kind: 'engineer'; label: '기사배정' }
  | { kind: 'doc'; to: number; label: string }
  | { kind: 'hold'; label: '보류' }
  | { kind: 'gu'; to: number; label: string; needDate: boolean };

function actionsFor(role: number): Action[] {
  const actions: Action[] = [];
  if ([3, 5].includes(role)) {
    actions.push({ kind: 'engineer', label: '기사배정' });
    actions.push({ kind: 'doc', to: 2, label: '제출 하기' });
  }
  if ([1, 2].includes(role)) {
    actions.push({ kind: 'doc', to: 3, label: '접수완료' });
    actions.push({ kind: 'doc', to: 2, label: '접수완료취소' });
  }
  if ([1, 2, 6, 7].includes(role)) {
    actions.push({ kind: 'hold', label: '보류' });
  }
  if (role === 6) {
    actions.push({ kind: 'gu', to: 2, label: '보조금 지급(시)', needDate: true });
    actions.push({ kind: 'gu', to: 1, label: '시보조금 취소', needDate: false });
  }
  if (role === 7) {
    actions.push({ kind: 'gu', to: 3, label: '보조금 지급(구)', needDate: true });
    actions.push({ kind: 'gu', to: 2, label: '구보조금 취소', needDate: false });
  }
  return actions;
}

/** 이 역할에 일괄 처리 버튼이 하나라도 있는가 — 체크박스 열 노출 여부에 쓴다. */
export function hasBatchActions(role: number): boolean {
  return actionsFor(role).length > 0;
}

export function BatchBar({
  role,
  ids,
  onDone,
}: {
  role: number;
  ids: number[];
  onDone: (message: string) => void;
}) {
  const [action, setAction] = useState<Action | null>(null);
  const actions = actionsFor(role);
  if (actions.length === 0) return null;

  return (
    <div className="batchbar">
      <span className="batchbar__count">{ids.length}건 선택</span>
      {actions.map((a) => (
        <button
          key={a.label}
          type="button"
          disabled={ids.length === 0}
          onClick={() => setAction(a)}
        >
          {a.label}
        </button>
      ))}

      {action !== null && (
        <BatchModal
          action={action}
          ids={ids}
          onClose={() => setAction(null)}
          onDone={(msg) => {
            setAction(null);
            onDone(msg);
          }}
        />
      )}
    </div>
  );
}

function BatchModal({
  action,
  ids,
  onClose,
  onDone,
}: {
  action: Action;
  ids: number[];
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [guDate, setGuDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 기사배정 — 레거시 팝업은 팀 → 기사 순으로 골랐지만 팀 목록 API 가 아직 없어
  // 기사(role=4)를 먼저 고르고 소속 팀(build_team_id)을 따라간다.
  const [keyword, setKeyword] = useState('');
  const [applied, setApplied] = useState('');
  const [engineerId, setEngineerId] = useState<number | ''>('');
  const [teamId, setTeamId] = useState<number | ''>('');
  const engineers = useAsync(
    () =>
      action.kind === 'engineer'
        ? api.listUsers({ role: 4, keyword: applied, size: 50 })
        : Promise.resolve(null),
    [applied, action.kind],
  );

  const selectedEngineer =
    engineerId === '' ? null : (engineers.data?.items.find((u) => u.id === engineerId) ?? null);
  // 기사의 소속 팀이 있으면 그대로 쓰고, 없으면 직접 입력을 받는다 (서버는 team_id 필수).
  const effectiveTeamId = selectedEngineer?.build_team_id ?? (teamId === '' ? null : teamId);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      if (action.kind === 'engineer') {
        if (engineerId === '' || effectiveTeamId === null) return;
        await api.batchEngineer({ ids, team_id: effectiveTeamId, engineer_id: engineerId });
      } else if (action.kind === 'hold') {
        await api.batchDocState({ ids, to: 4, reason: reason.trim() || undefined });
      } else if (action.kind === 'doc') {
        await api.batchDocState({ ids, to: action.to });
      } else {
        await api.batchGuPrice({
          ids,
          to: action.to,
          gu_date: action.needDate ? guDate || undefined : undefined,
        });
      }
      onDone(`${action.label} 처리되었습니다. (${ids.length}건)`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '처리과정에서 문제가 발생하였습니다.');
    } finally {
      setBusy(false);
    }
  }

  const engineerReady =
    action.kind !== 'engineer' || (engineerId !== '' && effectiveTeamId !== null);

  return (
    <Modal title={action.label} onClose={onClose}>
      <p>
        선택한 주문 <strong>{ids.length}건</strong>을 {action.label} 처리합니다. 조건에 맞지 않는
        주문이 하나라도 있으면 전체가 처리되지 않습니다.
      </p>

      {action.kind === 'engineer' && (
        <>
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault();
              setApplied(keyword.trim());
              setEngineerId('');
            }}
          >
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="기사 이름 검색"
            />
            <button type="submit">검색</button>
          </form>

          {engineers.loading && <Spinner label="기사 목록 불러오는 중…" />}
          {engineers.error && <ErrorBox message={engineers.error} onRetry={engineers.reload} />}
          {engineers.data && (
            <select
              value={engineerId}
              onChange={(e) => setEngineerId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">기사 선택 (총 {engineers.data.total}명)</option>
              {engineers.data.items.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name ?? u.user_id}
                </option>
              ))}
            </select>
          )}

          {selectedEngineer !== null && selectedEngineer.build_team_id === null && (
            <label className="labeled">
              팀 ID (기사의 소속 팀 정보가 없어 직접 입력)
              <input
                type="number"
                min={1}
                value={teamId}
                onChange={(e) => setTeamId(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </label>
          )}
        </>
      )}

      {action.kind === 'hold' && (
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="보류 이유"
        />
      )}

      {action.kind === 'gu' && action.needDate && (
        <label className="labeled">
          지급일
          <input type="date" value={guDate} onChange={(e) => setGuDate(e.target.value)} />
        </label>
      )}

      {error && <p className="error">{error}</p>}

      <div className="row">
        <button type="button" onClick={onClose}>
          취소
        </button>
        <button
          type="button"
          className="primary"
          disabled={busy || ids.length === 0 || !engineerReady}
          onClick={() => void run()}
        >
          {busy ? '처리 중…' : action.label}
        </button>
      </div>
    </Modal>
  );
}
